import jsPDF from "jspdf";

const loadImageDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmtMoney = (amount, currency = "MXN") => {
  if (amount === undefined || amount === null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return `$${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

export const generateReceiptPDF = async (client, info, { download = true } = {}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210;
  const navy = [10, 22, 51];
  const blue = [46, 107, 255];

  // Header band
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 42, "F");

  const logo = await loadImageDataUrl("/logo-uaicard.png");
  if (logo) {
    const h = 14;
    const w = (logo.w / logo.h) * h;
    doc.addImage(logo.data, "PNG", 16, 14, Math.min(w, 110), h, undefined, "FAST");
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("UAICARD BUSINESS", 16, 24);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("COMPROBANTE DE SERVICIO", W - 16, 20, { align: "right" });

  const folio = (client.id || "").replace(/-/g, "").slice(0, 8).toUpperCase() || "—";
  doc.setFontSize(8);
  doc.text(`Folio: ${folio}`, W - 16, 26, { align: "right" });
  doc.text(
    `Emitido: ${new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}`,
    W - 16,
    31,
    { align: "right" }
  );

  // Title
  let y = 60;
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Comprobante de servicio digital", 16, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  y += 7;
  doc.text("Portafolio / Tarjeta digital UAICARD BUSINESS", 16, y);

  // Data rows
  const billingLabel =
    client.billing_type === "permanent" ? "Permanente (pago único)" : "Renovación anual";
  const rows = [
    ["Cliente", client.name || "—"],
    ["WhatsApp", client.whatsapp || "—"],
    ["Fecha de inicio", fmtDate(client.start_date)],
    ["Modalidad", billingLabel],
    [
      "Próxima renovación",
      client.billing_type === "permanent" ? "No aplica (permanente)" : fmtDate(info?.renewal_date),
    ],
    ["Monto", fmtMoney(client.amount, client.currency || "MXN")],
  ];

  y += 12;
  const rowH = 12;
  doc.setDrawColor(226, 232, 240);
  rows.forEach(([label, value], i) => {
    const ry = y + i * rowH;
    if (i % 2 === 0) {
      doc.setFillColor(246, 248, 252);
      doc.rect(16, ry - 8, W - 32, rowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...navy);
    doc.text(label, 20, ry);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 41, 59);
    doc.text(String(value), W - 20, ry, { align: "right" });
  });

  y = y + rows.length * rowH + 6;
  doc.setDrawColor(...blue);
  doc.setLineWidth(0.6);
  doc.line(16, y, W - 16, y);

  // Notes
  y += 10;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  if (client.notes) {
    const lines = doc.splitTextToSize(`Notas: ${client.notes}`, W - 32);
    doc.text(lines, 16, y);
    y += lines.length * 5 + 4;
  }
  doc.text(
    client.billing_type === "permanent"
      ? "Servicio permanente. No requiere renovación anual."
      : "Este servicio se renueva cada año en la fecha indicada.",
    16,
    y
  );

  // Footer
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(16, 275, W - 16, 275);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text("UAICARD BUSINESS · Tarjetas digitales y portafolios web", 16, 282);
  doc.text("Gracias por tu confianza.", W - 16, 282, { align: "right" });

  const fileName = `comprobante-${(client.name || "cliente")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")}.pdf`;

  if (download) {
    doc.save(fileName);
    return { fileName };
  }
  return { blob: doc.output("blob"), fileName };
};
